import React, { useState } from 'react';
import { Box, Paper, useMediaQuery, IconButton, Drawer, List, ListItemButton, ListItemText, useTheme as useMuiTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { MenuList } from '../shared/MenuList';

interface MenuItem {
    id: string;
    label: string;
    component: React.ComponentType;
}

interface SubAppLayoutProps {
    menuItems: MenuItem[];
    defaultSelectedId?: string;
}

export const SubAppLayout: React.FC<SubAppLayoutProps> = ({
    menuItems,
    defaultSelectedId
}) => {
    const theme = useMuiTheme();
    const [selectedId, setSelectedId] = useState<string>(defaultSelectedId || menuItems[0].id);
    const SelectedComponent = menuItems.find(item => item.id === selectedId)?.component;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleSelect = (path: string) => {
        const id = path.replace('#', '');
        setSelectedId(id);
        if (isMobile) {
            setDrawerOpen(false);
        }
    };

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const drawerContent = (
        <Box
            sx={{ width: 220, p: 1 }}
            role="presentation"
            onClick={handleDrawerToggle}
            onKeyDown={handleDrawerToggle}
        >
            <List>
                {menuItems.map(item => (
                    <ListItemButton
                        key={item.id}
                        selected={selectedId === item.id}
                        onClick={() => handleSelect(`#${item.id}`)}
                        sx={{ borderRadius: 1.5, mb: 0.5 }}
                    >
                        <ListItemText primary={item.label} />
                    </ListItemButton>
                ))}
            </List>
        </Box>
    );

    return (
        <Box sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            flexDirection: isMobile ? 'column' : 'row',
        }}>
            {isMobile && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <ListItemText primary={menuItems.find(item => item.id === selectedId)?.label || 'Menu'} />
                </Box>
            )}
            {!isMobile ? (
                <Box sx={{
                    flexShrink: 0,
                    width: 220,
                    height: '100%',
                    backgroundColor: theme.palette.background.default,
                    borderRight: `1px solid ${theme.palette.divider}`,
                }}>
                    <MenuList
                        items={menuItems.map(item => ({
                            id: item.id,
                            label: item.label,
                            path: `#${item.id}`
                        }))}
                        onSelect={handleSelect}
                        selectedId={selectedId}
                    />
                </Box>
            ) : (
                <Drawer
                    anchor="left"
                    open={drawerOpen}
                    onClose={handleDrawerToggle}
                >
                    {drawerContent}
                </Drawer>
            )}
            <Box sx={{
                flexGrow: 1,
                p: isMobile ? 2 : 3,
                height: '100%',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.mode === 'light'
                    ? theme.palette.background.paper
                    : theme.palette.background.default,
            }}>
                <Paper elevation={0} sx={{
                    flex: 1,
                    p: { xs: 2, sm: 3 },
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    {SelectedComponent && <SelectedComponent />}
                </Paper>
            </Box>
        </Box>
    );
};
